/**
 * cl-buyers-procurement.js — handles the gated-documents request form on
 * /pages/buyers-procurement. Posts to the cl-attribution service's
 * POST /v1/procurement-docs (forms.chromalabel.com), the same backend the
 * site's quote form already uses (/v1/lead). No PII is stored client-side;
 * the only client-side bit is remembering whether the form was already
 * submitted this page view, to avoid accidental double-posts.
 */
(function () {
  "use strict";

  var ENDPOINT = "https://forms.chromalabel.com/v1/procurement-docs";
  // Mirrors the server's EMAIL_RE in util.ts — a loose syntax check, not a
  // business/MX check (that happens server-side, where it matters for the flag).
  var EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  function onReady(fn) {
    if (document.readyState !== "loading") fn();
    else document.addEventListener("DOMContentLoaded", fn);
  }

  onReady(function () {
    var form = document.getElementById("cbp-docs-form");
    if (!form) return;

    var statusEl = document.getElementById("cbp-docs-status");
    var fallbackEl = document.getElementById("cbp-docs-fallback");
    var submitBtn = form.querySelector('[type="submit"]');
    var submitting = false;

    // Only reveal the JS-powered form once its submit handler is actually
    // attached (below). Until then, the always-visible fallback paragraph
    // (email/phone) is the only path — never a raw, handler-less <form> GET
    // that would put name/email/company into the URL.
    form.hidden = false;
    if (fallbackEl) fallbackEl.hidden = true;

    // Show the "name our company as certificate holder" sub-option only
    // when the COI checkbox is actually checked.
    var coiCheck = document.getElementById("cbp-coi-check");
    var coiHolderWrap = document.getElementById("cbp-coi-holder-wrap");
    var coiHolderInput = document.getElementById("cbp-coi-holder");
    function syncCoiHolderVisibility() {
      var show = Boolean(coiCheck && coiCheck.checked);
      if (coiHolderWrap) coiHolderWrap.hidden = !show;
      if (!show && coiHolderInput) coiHolderInput.checked = false;
    }
    if (coiCheck) {
      coiCheck.addEventListener("change", syncCoiHolderVisibility);
      syncCoiHolderVisibility();
    }

    function setStatus(state, message) {
      if (!statusEl) return;
      statusEl.textContent = message;
      statusEl.setAttribute("data-state", state);
    }

    form.addEventListener("submit", function (evt) {
      evt.preventDefault();
      if (submitting) return;

      function fieldValue(fieldName) {
        return (form.elements[fieldName] && form.elements[fieldName].value || "").trim();
      }
      var name = fieldValue("name");
      var email = fieldValue("email");
      var company = fieldValue("company");
      var phone = fieldValue("phone");
      var street = fieldValue("street");
      var city = fieldValue("city");
      var region = fieldValue("region");
      var postalCode = fieldValue("postal_code");
      var country = fieldValue("country");
      var hp = fieldValue("hp");

      var docBoxes = form.querySelectorAll('input[name="docs"]:checked');
      var docs = [];
      for (var i = 0; i < docBoxes.length; i++) docs.push(docBoxes[i].value);
      // Only meaningful (and only sent) when COI is actually among the requested docs —
      // guards against a stale checked state surviving a previous reset.
      var coiNameHolder = docs.indexOf("coi") !== -1 && Boolean(coiHolderInput && coiHolderInput.checked);

      if (!name || !email || !company || !phone || !street || !city || !region || !postalCode || !country) {
        setStatus("err", "Please fill in every field — name, organization, business email, phone, and full street address (no PO boxes).");
        return;
      }
      if (!EMAIL_RE.test(email)) {
        setStatus("err", "That doesn't look like a valid email address — please double-check it.");
        return;
      }
      if (docs.length === 0) {
        setStatus("err", "Pick at least one document to request.");
        return;
      }

      submitting = true;
      if (submitBtn) submitBtn.disabled = true;
      setStatus("busy", "Sending your request…");

      fetch(ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "text/plain" }, // avoids a CORS preflight, matching the existing /v1/lead form
        body: JSON.stringify({
          name: name,
          email: email,
          company: company,
          phone: phone,
          street: street,
          city: city,
          region: region,
          postal_code: postalCode,
          country: country,
          docs: docs.join(","),
          coi_name_holder: coiNameHolder ? "1" : "",
          hp: hp,
        }),
      })
        .then(function (res) {
          if (!res.ok && res.status !== 400) throw new Error("http_" + res.status);
          return res.json().catch(function () { return {}; });
        })
        .then(function (data) {
          if (data && data.ok && data.path === "sent") {
            setStatus(
              "ok",
              "Thanks" + (name ? ", " + name.split(" ")[0] : "") + " — check your inbox, the document(s) are on their way."
            );
            form.reset();
            syncCoiHolderVisibility(); // form.reset() restores unchecked state but not the wrap's `hidden` attribute
          } else if (data && data.ok) {
            // path === "review" (or unspecified): every other outcome — free-webmail
            // email, document not yet approved for auto-send, etc. — routes to a person.
            setStatus(
              "ok",
              "Thanks" + (name ? ", " + name.split(" ")[0] : "") + " — we've got your request and a member of our team will follow up shortly."
            );
            form.reset();
            syncCoiHolderVisibility(); // form.reset() restores unchecked state but not the wrap's `hidden` attribute
          } else {
            setStatus("err", "We couldn't read that submission — double-check your name and email and try again, or call (800) 256-0435.");
          }
        })
        .catch(function () {
          setStatus("err", "Something went wrong sending that. Please try again, or email info@chromalabel.com.");
        })
        .finally(function () {
          submitting = false;
          if (submitBtn) submitBtn.disabled = false;
        });
    });
  });
})();
